/**
 * 数据验证工具类
 * 负责骨架图数据的验证
 * 
 * 验证内容：
 * - 坐标范围验证
 * - 骨架图数据完整性验证
 * - JSON格式验证
 */

import language from '@/utils/language'
import store from '@/store'

class DataValidator {
  /**
   * 获取翻译文本
   * @param {string} key - 翻译键
   * @param {Object} params - 参数对象
   * @returns {string} 翻译后的文本
   */
  t(key, params = {}) {
    const lang = store.state.app.language
    let text = language[key][lang]
    // 替换参数
    Object.keys(params).forEach(param => {
      text = text.replace(`{${param}}`, params[param])
    })
    return text
  }
  /**
   * 验证坐标
   * @param {number} lon - 经度
   * @param {number} lat - 纬度
   * @returns {{valid: boolean, errors: Array<string>}}
   */
  validateCoordinate(lon, lat) {
    const errors = []
    
    // 验证经度范围
    if (typeof lon !== 'number' || Number.isNaN(lon)) {
      errors.push(this.t('validatorLonMustBeNumber'))
    } else if (lon < -180 || lon > 180) {
      errors.push(this.t('validatorLonRange'))
    }
    
    // 验证纬度范围
    if (typeof lat !== 'number' || Number.isNaN(lat)) {
      errors.push(this.t('validatorLatMustBeNumber'))
    } else if (lat < -90 || lat > 90) {
      errors.push(this.t('validatorLatRange'))
    }
    
    return {
      valid: errors.length === 0,
      errors
    }
  }

  /**
   * 验证骨架图数据
   * @param {Object} graphData - 骨架图数据
   * @returns {{valid: boolean, errors: Array<string>}}
   */
  validateGraphData(graphData) {
    const errors = []
    
    // 验证基础信息
    if (!graphData) {
      errors.push(this.t('validatorGraphDataEmpty'))
      return { valid: false, errors }
    }
    
    if (!graphData.name || graphData.name.trim() === '') {
      errors.push(this.t('validatorGraphNameEmpty'))
    }
    
    if (!graphData.stationId) {
      errors.push(this.t('validatorStationRequired'))
    }
    
    // 验证节点
    if (!graphData.nodes || !Array.isArray(graphData.nodes)) {
      errors.push(this.t('validatorNodesFormatError'))
      return { valid: false, errors }
    }
    
    if (graphData.nodes.length === 0) {
      errors.push(this.t('validatorAtLeastOneNode'))
    }
    
    // 验证每个节点的坐标
    graphData.nodes.forEach((node, index) => {
      const lon = node.wgs84Lon || node.longitude || node.lon || node.lng
      const lat = node.wgs84Lat || node.latitude || node.lat
      
      const coordResult = this.validateCoordinate(lon, lat)
      if (!coordResult.valid) {
        errors.push(this.t('validatorNodeCoordInvalid', { index: index + 1 }) + ': ' + coordResult.errors.join(', '))
      }
      
      if (!node.id) {
        errors.push(this.t('validatorNodeMissingId', { index: index + 1 }))
      }
    })
    
    // 验证连线
    if (graphData.edges && Array.isArray(graphData.edges)) {
      const nodeIds = new Set(graphData.nodes.map(n => n.id))
      
      graphData.edges.forEach((edge, index) => {
        if (!edge.fromNodeId) {
          errors.push(this.t('validatorEdgeMissingFromNode', { index: index + 1 }))
        } else if (!nodeIds.has(edge.fromNodeId)) {
          errors.push(this.t('validatorEdgeFromNodeNotExist', { index: index + 1 }))
        }
        
        if (!edge.toNodeId) {
          errors.push(this.t('validatorEdgeMissingToNode', { index: index + 1 }))
        } else if (!nodeIds.has(edge.toNodeId)) {
          errors.push(this.t('validatorEdgeToNodeNotExist', { index: index + 1 }))
        }
        
        if (edge.fromNodeId === edge.toNodeId) {
          errors.push(this.t('validatorEdgeSameNode', { index: index + 1 }))
        }
      })
    }
    
    return {
      valid: errors.length === 0,
      errors
    }
  }

  /**
   * 验证JSON格式
   * @param {string} jsonString - JSON字符串
   * @returns {{valid: boolean, data: any, errors: Array<string>}}
   */
  validateJSON(jsonString) {
    const errors = []
    let data = null
    
    if (!jsonString || jsonString.trim() === '') {
      errors.push(this.t('validatorJsonEmpty'))
      return { valid: false, data, errors }
    }
    
    try {
      data = JSON.parse(jsonString)
    } catch (error) {
      errors.push(this.t('validatorJsonFormatError') + ': ' + error.message)
      return { valid: false, data, errors }
    }
    
    // 验证JSON数据结构
    if (!data) {
      errors.push(this.t('validatorJsonParsedEmpty'))
      return { valid: false, data, errors }
    }
    
    // 如果是数组，验证每个元素
    if (Array.isArray(data)) {
      if (data.length === 0) {
        errors.push(this.t('validatorJsonArrayEmpty'))
      }
      
      data.forEach((item, index) => {
        if (!item) {
          errors.push(this.t('validatorArrayElementEmpty', { index: index + 1 }))
          return
        }
        
        // 验证节点数据结构
        const hasLon = 'lon' in item || 'longitude' in item || 'lng' in item
        const hasLat = 'lat' in item || 'latitude' in item
        
        if (!hasLon || !hasLat) {
          errors.push(this.t('validatorArrayElementMissingCoord', { index: index + 1 }))
        }
      })
    } else if (typeof data === 'object') {
      // 如果是对象，验证是否包含nodes数组
      if ('nodes' in data && Array.isArray(data.nodes)) {
        if (data.nodes.length === 0) {
          errors.push(this.t('validatorNodesArrayEmpty'))
        }
      } else {
        // 验证单个节点对象
        const hasLon = 'lon' in data || 'longitude' in data || 'lng' in data
        const hasLat = 'lat' in data || 'latitude' in data
        
        if (!hasLon || !hasLat) {
          errors.push(this.t('validatorObjectMissingCoord'))
        }
      }
    } else {
      errors.push(this.t('validatorJsonInvalidFormat'))
    }
    
    return {
      valid: errors.length === 0,
      data,
      errors
    }
  }

  /**
   * 验证节点数据
   * @param {Object} node - 节点数据
   * @returns {{valid: boolean, errors: Array<string>}}
   */
  validateNode(node) {
    const errors = []
    
    if (!node) {
      errors.push(this.t('validatorNodeDataEmpty'))
      return { valid: false, errors }
    }
    
    if (!node.id) {
      errors.push(this.t('validatorNodeIdEmpty'))
    }
    
    const lon = node.wgs84Lon || node.longitude || node.lon || node.lng
    const lat = node.wgs84Lat || node.latitude || node.lat
    
    const coordResult = this.validateCoordinate(lon, lat)
    if (!coordResult.valid) {
      errors.push(...coordResult.errors)
    }
    
    return {
      valid: errors.length === 0,
      errors
    }
  }

  /**
   * 验证连线数据
   * @param {Object} edge - 连线数据
   * @param {Array<number|string>} validNodeIds - 有效的节点ID列表
   * @returns {{valid: boolean, errors: Array<string>}}
   */
  validateEdge(edge, validNodeIds = []) {
    const errors = []
    
    if (!edge) {
      errors.push(this.t('validatorEdgeDataEmpty'))
      return { valid: false, errors }
    }
    
    if (!edge.fromNodeId) {
      errors.push(this.t('validatorEdgeMissingFromNodeId'))
    } else if (validNodeIds.length > 0 && !validNodeIds.includes(edge.fromNodeId)) {
      errors.push(this.t('validatorEdgeFromNodeNotExist'))
    }
    
    if (!edge.toNodeId) {
      errors.push(this.t('validatorEdgeMissingToNodeId'))
    } else if (validNodeIds.length > 0 && !validNodeIds.includes(edge.toNodeId)) {
      errors.push(this.t('validatorEdgeToNodeNotExist'))
    }
    
    if (edge.fromNodeId === edge.toNodeId) {
      errors.push(this.t('validatorEdgeSameNodes'))
    }
    
    return {
      valid: errors.length === 0,
      errors
    }
  }
}

// 导出单例
export default new DataValidator()
